import { ImageName } from "@mcleod/images";
import { Button, ClickEvent, ContextMenuItemCreator, ContextMenuLabelProps, CrudDecorator, DataSourceMode, Label, Layout, Panel, Snackbar, Table, TableRow, TableSelectionEvent } from "@mcleod/components";
import { AutogenLayoutInvoiceHistoryToolPanel } from "./autogen/AutogenLayoutInvoiceHistoryToolPanel";
import { InvoiceHistoryList } from "./InvoiceHistoryList";
import { CommonDialogs } from "@mcleod/common";
import { Api, ModelRow, UrlUtil } from "@mcleod/core";
import { EdiBilling } from "./EdiBilling";

interface FoundOrderId {
    order_id: string;
    direction: string;
}
export class InvoiceHistoryToolPanel extends AutogenLayoutInvoiceHistoryToolPanel {
    table: Table;

    private getAllVisibleButtons(): Button[] {
        return this.panelButtons?.findComponentsByType(Button)?.filter(button => button.visible);
    }

    initialize(table?: Table) {
        this.table = table;
        if (this.getAllVisibleButtons()?.length > 0) {
            this.table?.addSelectionListener((event: TableSelectionEvent) => this.tableInvoiceHistoryOnSelect(event))
        } else {
            this.removeAll();
        }
    }

    tableInvoiceHistoryOnSelect(event: TableSelectionEvent) {
        const selectedRows = this.table?.selectedRows || [];
        const hasSelection = selectedRows.length > 0;

        this.getAllVisibleButtons().forEach(button => {
            button.disabledTooltip = null;
            button.enabled = hasSelection && selectedRows.every(row => row.data != null);
            this.syncButtonState(button, selectedRows);
        })
    }

    private syncButtonState(button: Button, selectedRows: TableRow[]) {
        if (button === this.buttonManuallyTransmit) {
            this.syncButtonManuallyTransmit(selectedRows);
        } else if (button === this.buttonBalanceDue) {
            this.syncButtonBalanceDue(selectedRows);
        }
    }

    private syncButtonManuallyTransmit(selectedRows: TableRow[]) {
        const selectedRowsLocal = selectedRows || [];
        const partnerIdSet = new Set<string>();
        let invalidValues = false;
        selectedRowsLocal.forEach((tableRow) => {
            const partnerId = tableRow.data.get("partner_id");
            const readyToSend = tableRow.data.get("ready_to_send");
            const hasError = tableRow.data.get("has_error_out");
            const direction = tableRow.data.get("direction");
            if (partnerId) {
                if (!partnerIdSet.has(partnerId)) {
                    partnerIdSet.add(partnerId);
                }
                if (readyToSend !== "Y" || hasError !== "--" || direction !== "O") {
                    invalidValues = true;
                }
            }
        });
        if (partnerIdSet.size > 1 || invalidValues) {
            this.buttonManuallyTransmit.enabled = false;
            this.buttonManuallyTransmit.disabledTooltip = "Cannot transmit batches with multiple partner IDs or invalid values.";
        } else if (partnerIdSet.size === 1) {
            this.buttonManuallyTransmit.enabled = true;
            this.buttonManuallyTransmit.disabledTooltip = null;
        } else {
            this.buttonManuallyTransmit.enabled = false;
            this.buttonManuallyTransmit.disabledTooltip = "Please select one or more valid records to transmit.";
        }
    }

    private syncButtonBalanceDue(selectedRows: TableRow[]) {
        const selectedRowsLocal = selectedRows || [];
        const orderIdSet: FoundOrderId[] = [];
        selectedRowsLocal.forEach((tableRow) => {
            const orderId = tableRow.data.get("order_id");
            const direction = tableRow.data.get("direction");
            if (orderId) {
                if (!orderIdSet.some(item => item.order_id === orderId)) {
                    orderIdSet.push({ order_id: orderId, direction });
                }
            }
        });
        if (orderIdSet.length > 1) {
            this.buttonBalanceDue.enabled = false;
            this.buttonBalanceDue.disabledTooltip = "Cannot create balance due with more than one selected record.";
        } else if (orderIdSet.length === 1 && orderIdSet[0].direction === "O") {
            this.buttonBalanceDue.enabled = true;
            this.buttonBalanceDue.disabledTooltip = null;
        } else if (orderIdSet.length === 1 && orderIdSet[0].direction === "I") {
            this.buttonBalanceDue.enabled = false;
            this.buttonBalanceDue.disabledTooltip = "Cannot create balance due for inbound invoices.";
        } else {
            this.buttonBalanceDue.enabled = false;
            this.buttonBalanceDue.disabledTooltip = "Please select one record to create a balance due.";
        }
    }

    async generateYesNoMessage(selected: any[]): Promise<string> {
        const response = await Api.post("lme/datafusion/number-of-transactions", { transmit_ids: selected.sort((a, b) => a.batch - b.batch), is_billing: true });
        return response?.data[0]?.transactions_message;
    }

    static getContextMenuItems(toolPanel: InvoiceHistoryToolPanel, tableRow: TableRow): Partial<ContextMenuLabelProps>[] {
        const selectedRows = tableRow?.table?.selectedRows;
        const invoiceId = selectedRows?.length > 1 && selectedRows?.includes(tableRow)
            ? null : tableRow?.data?.get("id");
        return InvoiceHistoryList.addNavigationContextMenuItems(toolPanel.getContextMenuItems(tableRow), invoiceId)
    }

    public getContextMenuItems(tableRow: TableRow): Partial<ContextMenuLabelProps>[] {
        const selectedRows = tableRow.table.selectedRows;
        if (!selectedRows.includes(tableRow)) {
            tableRow.table.selectedRow = tableRow;
        }
        const fromButtons = this.getAllVisibleButtons().map(button => ContextMenuItemCreator.fromButton(button));
        return [...fromButtons];
    }

    async onClickManualTransmit(event: ClickEvent) {
        try {
            const selected = this.returnedSelectedRows();
            const count = selected.length;
            if (count > 0) {
                const messageGenerated = await this.generateYesNoMessage(selected);
                const message = "Do you want to submit\n\n" + messageGenerated + "\nfor transmission?";
                CommonDialogs.showYesNo(message, "Manual Transmit",
                    { titleProps: { imageName: ImageName.circleX, imageColor: "primary.light" } }).then(async yes => {
                        this.buttonManuallyTransmit.busy = true;
                        if (yes) {
                            await Api.post("lme/datafusion/manual-edi-transmit", { transmit_ids: selected, is_billing: true }).then(result => {
                                //ERROR SNACKBAR
                                const { data = [] } = result || {};
                                const { error: errors = [], error_number: errorCount = 0 } = data[0] || {};

                                if (errorCount > 0) {
                                    const successCount = count - errorCount;
                                    const message = `${successCount} of ${count} batches were submitted for transmission.`;
                                    const errorDetails = Array.isArray(errors) ? errors.join(", ") : errors;
                                    Snackbar.showWarningSnackbar(`${message}\n${errorDetails}`);
                                } else {
                                    Snackbar.showSuccessSnackbar(`The selected batches (${count}) have been submitted for transmission.`);
                                }
                            }).catch(error => {
                                console.error("Error transmitting invoices", error);
                            });
                        }
                    }).finally(() => {
                        this.buttonManuallyTransmit.busy = false;
                    });
            }
        } catch (error) {
            Snackbar.showWarningSnackbar("Error when attempting to manual transmit: " + error.message);
            this.buttonManuallyTransmit.busy = false;
        }
    }

    async onClickBalanceDue(event: ClickEvent) {
        try {
            const selected = this.table?.selectedRows[0] || null;
            if (selected) {
                const modelRow: ModelRow = selected.data;
                const apiParams = {
                    order_id: modelRow.get("order_id"),
                    is_split_bill: modelRow.getBoolean("is_split_bill"),
                    customer_id: modelRow.get("customer_id"),
                    partner_id: modelRow.get("partner_id"),
                    version: modelRow.get("version"),
                    alt_partner_id: modelRow.get("alt_partner_id")
                }
                Api.search("lme/datafusion/add-edi-balancedue", apiParams).then(async apiResponse => {
                    if (apiResponse?.data && apiResponse.data.length > 0) {
                        const ediLayout = Layout.getLayout("lme/datafusion/EdiBilling") as EdiBilling;
                        ediLayout.addLayoutLoadListener(() => {
                                ediLayout.textboxInvoiceNoString.enabled = true;
                                ediLayout.textboxShipDate.required = true;
                                ediLayout.textboxDeliveryDate.required = true;
                            });
                        const urlParam = { mode: "add", key: apiResponse.data[0].id };
                        const url = "/lme/datafusion/EdiBilling" + UrlUtil.buildQueryString(urlParam);
                        const ediPanel = new CrudDecorator({
                            layout: ediLayout,
                            mode: DataSourceMode.ADD,
                            pseudoNavigateUrl: url,
                            headerProps: {
                                showClose: true,
                                showSaveAndClose: true,
                            },
                            key: apiResponse.data[0].id,
                            providedApiData: apiResponse.data[0],
                        }) as any;
                        await ediPanel.slideIn();

                        // Check for warning and display snackbar after slide-in to ensure it appears on top
                        if (apiResponse.data[0].warning) {
                            const warningPanel = new Panel();
                            const warningLabel = new Label({ text: apiResponse.data[0].warning });
                            warningPanel.add(warningLabel);

                            Snackbar.showWarningSnackbar(warningPanel, {
                                millisUntilDismissal: 5000, // Auto-dismiss after 5 seconds
                                targetPanel: ediPanel // Show snackbar on the EDI panel to ensure proper z-index
                            });
                        }
                    } else {
                        Snackbar.showWarningSnackbar("Failed to create Balance Due transaction.");
                    }
                }).catch(error => {
                    Snackbar.showWarningSnackbar("Error creating Balance Due transaction: " + error.message);
                });
            }
        } catch (error) {
            Snackbar.showWarningSnackbar("Error when attempting to create Balance Due transaction: " + error.message);
            this.buttonManuallyTransmit.busy = false;
        }
    }

    returnedSelectedRows(): any[] {
        const selectedRows = this.table?.selectedRows || [];
        // Use a Map to track unique partner_id and batch pairs
        const uniquePairMap = new Map<string, any>();

        // Add each row to the map with partner_id:batch as the key
        selectedRows.forEach((tableRow) => {
            const partnerId = tableRow.data.get("partner_id");
            const batch = tableRow.data.get("batch");
            const readyToSend = tableRow.data.get("ready_to_send");
            if (partnerId && readyToSend === "Y") {
                const compositeKey = `${partnerId}:${batch}`;
                if (!uniquePairMap.has(compositeKey)) {
                    uniquePairMap.set(compositeKey, {
                        id: tableRow.data.get("id"),
                        customer_id: tableRow.data.get("customer_id"),
                        partner_id: partnerId,
                        direction: tableRow.data.get("direction"),
                        partner_name: tableRow.data.get("edibilling_profile.partner_name"),
                        batch: batch
                    });
                }
            }
        });

        // Return the values from the map
        return Array.from(uniquePairMap.values());
    }
}
